// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle, Fingerprint } from '../data';
import { GRADE_ORDER, gradeRank } from '../data';
import { num, pct1, signed, safe, tipAttr, escapeHtml, debounce, WITHHELD } from '../format';
import { openDrawer } from '../components/overlay';

/**
 * The only per-agency view, and it sorts by SHAPE rather than by size. Each
 * employer is a tiny thirteen-bar grade profile, so a top-heavy agency and a
 * frontline one are distinguishable at a glance without reading a single number.
 *
 * A confidentialised grade draws a hatched notch, never a zero-height bar, and
 * each card states how many of its grades were withheld.
 */

export function renderAgencies(root: HTMLElement, b: Bundle): void {
  let query = '';
  let sortBy: 'seniority' | 'size' | 'name' = 'seniority';
  let showSubs = true;

  root.innerHTML = `
    <div class="view-head">
      <h2>Every employer in the service</h2>
      <p>All ${b.agencies.fingerprints.length} APS employers as grade profiles — the mix of junior to senior staff in each. Departments are listed flush; the agencies in their <span class="glossary-link" data-term="portfolio">portfolio</span> are indented. Click any employer for its full scorecard.</p>
    </div>
    <div class="controls">
      <input type="search" id="agency-search" placeholder="Search agencies…" aria-label="Search agencies" />
      <div class="control-group">
        <span class="control-label">Sort</span>
        <div class="seg" role="group" aria-label="Sort agencies">
          <button type="button" data-sort="seniority" aria-pressed="true">By seniority</button>
          <button type="button" data-sort="size" aria-pressed="false">By size</button>
          <button type="button" data-sort="name" aria-pressed="false">A–Z</button>
        </div>
      </div>
      <label style="display:inline-flex;align-items:center;gap:6px;font-size:var(--font-size-sm)">
        <input type="checkbox" id="show-subs" checked /> Include portfolio agencies
      </label>
    </div>
    <p class="section-sub" id="agency-count"></p>
    <div id="fingerprints"></div>`;

  const grades = b.agencies.gradeOrder.filter((g) => GRADE_ORDER.includes(g))
    .sort((a, c) => gradeRank(a) - gradeRank(c));

  const metricOf = new Map(b.agencies.agencies.map((a) => [a.slug, a]));
  const flowOf = new Map(b.agencies.flows.map((f) => [f.slug, f]));

  /** Seniority centre of mass: the average grade rank, weighted by headcount. */
  function seniority(fp: Fingerprint): number {
    let n = 0, d = 0;
    for (const g of fp.grades) {
      if (g.v === null) continue;
      n += gradeRank(g.g) * g.v; d += g.v;
    }
    return d ? n / d : 0;
  }

  function total(fp: Fingerprint): number {
    return fp.grades.reduce((s, g) => s + (g.v ?? 0), 0);
  }

  function draw(): void {
    let list = b.agencies.fingerprints.filter((f) => showSubs || !f.isSub);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((f) => f.name.toLowerCase().includes(q) || (f.portfolio ?? '').toLowerCase().includes(q));
    }
    list = [...list].sort((a, c) =>
      sortBy === 'size' ? total(c) - total(a)
        : sortBy === 'name' ? a.name.localeCompare(c.name)
          : seniority(c) - seniority(a));

    root.querySelector('#agency-count')!.textContent =
      `${list.length} of ${b.agencies.fingerprints.length} employers shown. `
      + (sortBy === 'seniority' ? 'Most senior-weighted first.' : sortBy === 'size' ? 'Largest first.' : 'Alphabetical.');

    root.querySelector('#fingerprints')!.innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(min(230px,100%),1fr));gap:var(--space-md)">
      ${list.map((fp) => {
        const t = total(fp);
        const withheld = fp.grades.filter((g) => g.s).length;
        const seniorShare = t ? (fp.grades.filter((g) => gradeRank(g.g) >= gradeRank('EL 1'))
          .reduce((s, g) => s + (g.v ?? 0), 0) / t) * 100 : null;
        const bw = 210 / grades.length;
        // Each profile is scaled to its OWN maximum so the shape reads; the
        // tooltip always carries the absolute numbers.
        const localMax = Math.max(...fp.grades.map((g) => g.v ?? 0), 1);
        const bars = grades.map((g, i) => {
          const cell = fp.grades.find((x) => x.g === g);
          const v = cell?.v ?? null;
          const supp = !!cell?.s;
          const h = v === null ? 0 : (v / localMax) * 40;
          const x = i * bw;
          if (supp) {
            return `<rect class="supp-hatch" x="${x.toFixed(1)}" y="34" width="${(bw - 1.6).toFixed(1)}" height="6" />`;
          }
          return `<rect class="bar" x="${x.toFixed(1)}" y="${(44 - h).toFixed(1)}" width="${(bw - 1.6).toFixed(1)}"
            height="${Math.max(0.6, h).toFixed(1)}" fill="${gradeRank(g) >= gradeRank('EL 1') ? '#12385c' : '#3d9fb5'}"
            data-tip="${escapeHtml(tipAttr([`${fp.name}`, `${g}: ${num(v)}`, `${pct1(t ? ((v ?? 0) / t) * 100 : null)} of this employer`]))}"><title></title></rect>`;
        }).join('');
        return `<button type="button" class="panel" data-agency="${escapeHtml(fp.slug)}"
            style="text-align:left;cursor:pointer;padding:var(--space-md);border-color:var(--border-subtle);background:var(--bg-panel);font:inherit;color:inherit;width:100%;min-width:0">
          <div style="font-size:var(--font-size-sm);font-weight:620;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0">
            ${fp.isSub ? '<span style="color:var(--text-tertiary)">↳ </span>' : ''}${escapeHtml(fp.name)}
          </div>
          <div style="font-size:var(--font-size-xs);color:var(--text-secondary);margin-bottom:4px">
            <span class="num">${num(t)}</span> staff · <span class="num">${pct1(seniorShare)}</span> EL 1 and above
          </div>
          <svg class="chart" viewBox="0 0 210 46" role="img" aria-label="${escapeHtml(fp.name)} grade profile" style="max-width:210px">
            ${bars}
          </svg>
          ${withheld ? `<div style="font-size:var(--font-size-xs);color:var(--text-tertiary);margin-top:3px">${withheld} grade${withheld > 1 ? 's' : ''} withheld</div>` : ''}
        </button>`;
      }).join('')}</div>`;
  }

  function openAgency(slug: string): void {
    const fp = b.agencies.fingerprints.find((f) => f.slug === slug);
    if (!fp) return;
    const m = metricOf.get(slug);
    const f = flowOf.get(slug);
    const t = total(fp);
    const jf = b.agencies.jobFamily.rows.find((r) => r.label.replace(/^[-–—]\s+/, '') === fp.name);

    const metricRows: [string, string][] = m ? [
      ['Headcount', num(m.headcount)],
      ['Women', pct1(m.female)],
      ['Indigenous', pct1(m.indigenous)],
      ['With a disability', pct1(m.disability)],
      ['Culturally and linguistically diverse', pct1(m.cald)],
      ['Non-ongoing', pct1(m.nonOngoing)],
      ['Part-time (ongoing)', pct1(m.partTime)],
      ['At the APS classification level', pct1(m.atApsLevel)],
      ['Mean age', m.meanAge === null ? WITHHELD : `${m.meanAge.toFixed(1)} years`],
      ['Median length of service', m.medianLos === null ? WITHHELD : `${m.medianLos.toFixed(1)} years`],
      ['Worked in only one agency', pct1(m.oneAgencyOnly)],
      ['Bachelor degree or higher', pct1(m.bachelorPlus)],
      ['Exit rate (from this agency)', pct1(m.exitRate)],
      ['Separation rate (from the APS)', pct1(m.separationRate)],
    ] : [];

    const gradeRows = grades.map((g) => {
      const cell = fp.grades.find((x) => x.g === g);
      return `<tr><td>${escapeHtml(g)}</td>
        <td class="n">${cell?.s ? `<span class="cell-supp">${WITHHELD}</span>` : num(cell?.v ?? null)}</td>
        <td class="n">${cell?.s ? '' : pct1(t ? ((cell?.v ?? 0) / t) * 100 : null)}</td></tr>`;
    }).join('');

    openDrawer(`
      <h3>${escapeHtml(fp.name)}</h3>
      <p class="drawer-sub">${fp.isSub && fp.portfolio ? `In the ${escapeHtml(fp.portfolio)} portfolio` : 'Department of state'} · as at ${escapeHtml(b.meta.asAt)}</p>
      <h4>Grade profile</h4>
      <div class="scroller"><table class="data">
        <thead><tr><th>Grade</th><th class="n">Staff</th><th class="n">Share</th></tr></thead>
        <tbody>${gradeRows}</tbody></table></div>
      ${metricRows.length ? `<h4>Scorecard</h4><dl class="kv">${metricRows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${v}</dd>`).join('')}</dl>` : ''}
      ${f ? `<h4>Mobility</h4><dl class="kv">
        <dt>Promoted within the agency</dt><dd>${num(f.within)}</dd>
        <dt>Promoted in from another agency</dt><dd>${f.fromSuppressed ? WITHHELD : num(f.from)}</dd>
        <dt>Promoted out to another agency</dt><dd>${f.toSuppressed ? WITHHELD : num(f.to)}</dd>
        <dt>Net gain or loss</dt><dd>${signed(f.net)}</dd>
      </dl>` : ''}
      ${jf ? `<h4>Job families</h4><dl class="kv">${jf.cells
        .filter((c) => c.value !== null || c.suppressed)
        .sort((a, c) => (c.value ?? 0) - (a.value ?? 0))
        .slice(0, 8)
        .map((c) => `<dt>${escapeHtml(c.key)}</dt><dd>${c.suppressed ? WITHHELD : num(c.value)}</dd>`).join('')}</dl>` : ''}
      <p class="section-sub" style="margin-top:var(--space-lg)">
        A full stop in the source means the figure was <span class="glossary-link" data-term="suppressed">confidentialised</span> because too few people sit behind it — it is shown here as “withheld”, never as zero.
        Agency names and boundaries change with <span class="glossary-link" data-term="mog">machinery of government</span> changes, so comparing one agency across many years is unreliable.
      </p>`);
  }

  const search = root.querySelector<HTMLInputElement>('#agency-search')!;
  search.addEventListener('input', debounce(() => { query = search.value.trim(); draw(); }, 300));
  root.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((btn) => {
    btn.addEventListener('click', () => {
      sortBy = btn.dataset.sort as typeof sortBy;
      root.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((x) => x.setAttribute('aria-pressed', String(x === btn)));
      draw();
    });
  });
  root.querySelector<HTMLInputElement>('#show-subs')!.addEventListener('change', (e) => {
    showSubs = (e.target as HTMLInputElement).checked; draw();
  });
  root.addEventListener('click', (e) => {
    const el = (e.target as Element).closest('[data-agency]');
    if (el) openAgency(el.getAttribute('data-agency')!);
  });

  draw();
  void safe;
}
